import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import feather from 'feather-icons';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import useOutletRefreshSignal from '../hooks/useOutletRefreshSignal';
import { Helmet } from 'react-helmet-async';
import serverClient from '../api/ServerClient';
import { DeleteToken } from '../auth/Auth';
import Alert from '../components/Alert';
import Loader from '../components/Loader';
import { formatDate, getTransactionClass, getTransactionImage } from '../util/transactionHelper';
import UpdateTransactionBottomSheet from '../components/Modals/UpdateTransactionBottomSheet';
import CustomDateModal from '../components/Modals/CustomDateModal';
import SuccessToast from '../toast/SuccessToast';
import { amountDisplay, dateDisplay, getUserTimezone, numberLanguageWise } from '../util/helper';
import BottomNavbar from '../components/Home/BottomNavbar';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import WarningToast from './WarningToast';
import { useLanguage } from '../context/LanguageContext';
import { getPageMeta } from '../util/seoHelper';
import EmptyState from '../components/TransactionHistory/EmptyState';
import Filters from '../components/TransactionHistory/Filters';
import SearchInput from '../components/TransactionHistory/SearchInput';
import { FiCalendar } from 'react-icons/fi';
import BackButton from '../components/BackButton';
import '../styles/TransactionHistoryPage.css';

const inferInitialPeriodType = ({ routePeriodType, routeStartDate, routeEndDate, timeZone }) => {
    if (routePeriodType === 'year' || routePeriodType === 'yearly') return 'year';
    if (routePeriodType === 'lastSixMonth') return 'lastSixMonth';
    if (routePeriodType === 'custom') return 'custom';
    if (!routeStartDate || !routeEndDate) return 'month';

    const start = dayjs(routeStartDate).tz(timeZone);
    const end = dayjs(routeEndDate).tz(timeZone);

    if (!start.isValid() || !end.isValid()) return 'month';

    const formattedStart = start.format('YYYY-MM-DD');
    const formattedEnd = end.format('YYYY-MM-DD');

    if (
        formattedStart === start.startOf('year').format('YYYY-MM-DD') &&
        formattedEnd === start.endOf('year').format('YYYY-MM-DD')
    ) {
        return 'year';
    }

    if (
        formattedStart === start.startOf('month').format('YYYY-MM-DD') &&
        formattedEnd === start.endOf('month').format('YYYY-MM-DD')
    ) {
        return 'month';
    }

    return 'custom';
};

// --- Main Component ---
const PAGE_SIZE = 50;
const TransactionHistory = () => {
    dayjs.extend(utc);
    dayjs.extend(timezone);
    const timeZone = getUserTimezone();
    const navigate = useNavigate();
    const refreshSignal = useOutletRefreshSignal();
    const { t, language } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [error, setError] = useState(null);
    const [accounts, setAccounts] = useState(null);
    const [categories, setCategories] = useState(null);
    const [selectedAccount, setSelectedAccount] = useState('');
    const [transactionsData, setTransactionsData] = useState({});
    const [transaction, setTransaction] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [showToastForDelete, setShowToastForDelete] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedType, setSelectedType] = useState('');
    const location = useLocation();
    const routeStartDate = location.state?.startDate;
    const routeEndDate = location.state?.endDate;
    const routePeriodType = location.state?.periodType || location.state?.reportingType;
    const [startDate, setStartDate] = useState(() => {
        return routeStartDate
            ? dayjs(routeStartDate).tz(timeZone)
            : dayjs().tz(timeZone).startOf('month');
    });
    const [endDate, setEndDate] = useState(() => {
        return routeEndDate ? dayjs(routeEndDate).tz(timeZone) : dayjs().tz(timeZone);
    });
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const [periodType, setPeriodType] = useState(() =>
        inferInitialPeriodType({
            routePeriodType,
            routeStartDate,
            routeEndDate,
            timeZone,
        })
    ); // 'month' | 'lastSixMonth' | 'year' | 'custom'
    const isFirstSearch = useRef(true);

    // --- Pagination (infinite scroll) ---
    const [loadingMore, setLoadingMore] = useState(false);
    const offsetRef = useRef(0);
    const hasMoreRef = useRef(true);
    const loadingMoreRef = useRef(false);
    // Guards against race conditions: only the latest request is allowed to update state.
    const requestIdRef = useRef(0);
    const resetInFlightRef = useRef(false);
    // Skip the refreshSignal effect on mount (initial signal is 0, not undefined)
    const isFirstRefreshSignal = useRef(true);

    const initialCategoryId = location.state?.selectedCategoryId ?? '';
    const [selectedCategory, setSelectedCategory] = useState(initialCategoryId);

    // --- Memoized Options ---
    const accountOptions = useMemo(
        () =>
            accounts
                ? [
                      { value: '', label: t('allWallets') },
                      ...accounts.map((a) => ({
                          value: a.id,
                          label: a.name,
                      })),
                  ]
                : [{ value: '', label: t('allWallets') }],
        [accounts, t]
    );
    const categoryOptions = useMemo(() => {
        if (!categories) return [{ value: '', label: t('allCategories') }];
        if (selectedType) {
            return categories
                .filter((cat) =>
                    selectedType === 'expense'
                        ? cat.type === 'Expense'
                        : selectedType === 'income'
                          ? cat.type === 'Income'
                          : true
                )
                .map((cat) => ({ value: cat.id, label: cat.name }));
        }
        return [
            { value: '', label: t('allCategories') },
            ...categories.map((c) => ({ value: c.id, label: c.name })),
        ];
    }, [categories, selectedType, t]);
    const periodLabel = useMemo(() => {
        if (periodType === 'lastSixMonth') return t('lastSixMonth');
        if (periodType === 'year') return numberLanguageWise(startDate.format('YYYY'), language);
        if (periodType === 'custom') {
            return `${dateDisplay(startDate, language, 'short')} - ${dateDisplay(endDate, language, 'short')}`;
        }
        return dateDisplay(startDate, language, 'monthOnly');
    }, [periodType, startDate, endDate, language, t]);

    // --- Handlers ---
    const handleSearchChange = useCallback((e) => {
        setSearchTerm(e.target.value.toLowerCase());
    }, []);
    const resetFilters = useCallback(() => {
        setSelectedType('');
        setSearchTerm('');
        setSelectedCategory(''); // Reset to empty string
        setSelectedAccount('');
    }, []);
    const handleShowBottomSheet = useCallback((transaction) => {
        setTransaction(transaction);
        setShowModal(true);
    }, []);
    const handleCloseBottomSheet = useCallback(() => setShowModal(false), []);

    // --- Date Handlers ---
    const shiftPeriod = useCallback(
        (amount) => {
            const unit = periodType === 'year' ? 'year' : 'month';
            if (unit === 'month') setPeriodType('month');
            const newStart = startDate.add(amount, unit).startOf(unit).tz(timeZone);
            setStartDate(newStart);
            setEndDate(newStart.endOf(unit));
        },
        [periodType, startDate, timeZone]
    );
    const handlePrevPeriod = useCallback(() => shiftPeriod(-1), [shiftPeriod]);
    const handleThisMonth = useCallback(() => {
        const start = dayjs().tz(timeZone).startOf('month');
        const end = dayjs().tz(timeZone).endOf('month');
        setPeriodType('month');
        setStartDate(start);
        setEndDate(end);
    }, [timeZone]);
    const handleNextPeriod = useCallback(() => shiftPeriod(1), [shiftPeriod]);
    const handlePreviousSixMonth = useCallback(() => {
        const end = dayjs().tz(timeZone).endOf('day');
        const start = end.subtract(6, 'month').startOf('day');
        setPeriodType('lastSixMonth');
        setStartDate(start);
        setEndDate(end);
    }, [timeZone]);
    const handleThisYear = useCallback(() => {
        const now = dayjs().tz(timeZone);
        const start = now.startOf('year');
        const end = now.endOf('year');
        setPeriodType('year');
        setStartDate(start);
        setEndDate(end);
    }, [timeZone]);
    const handleApplyCustomDate = useCallback(() => {
        if (!customStartDate || !customEndDate) {
            alert(t('selectBothDates'));
            return;
        }
        const start = dayjs(customStartDate).tz(timeZone).startOf('day');
        const end = dayjs(customEndDate).tz(timeZone).endOf('day');
        if (start.isAfter(end)) {
            alert(t('startDateBeforeEndDate'));
            return;
        }
        setPeriodType('custom');
        setStartDate(start);
        setEndDate(end);
        const modalEl = document.getElementById('period');
        const modalInstance = window.bootstrap?.Modal?.getInstance(modalEl);
        modalInstance?.hide();
    }, [customStartDate, customEndDate, timeZone, t]);

    // --- Data Fetchers ---
    // Fetches one page of transactions. `reset: true` starts over from offset 0 (filters/search
    // changed or data needs a refresh); `reset: false` appends the next page (infinite scroll).
    const fetchTransactionsPage = useCallback(
        async ({ reset, useSearchLoading = false }) => {
            // Don't load more pages while a reset fetch is in flight or nothing is left
            if (
                !reset &&
                (loadingMoreRef.current || resetInFlightRef.current || !hasMoreRef.current)
            ) {
                return;
            }

            const setBusy = useSearchLoading ? setSearchLoading : setLoading;
            // Every request gets a unique id; only the latest one may update state.
            const requestId = ++requestIdRef.current;

            try {
                if (reset) {
                    offsetRef.current = 0;
                    hasMoreRef.current = true;
                    resetInFlightRef.current = true;
                    setBusy(true);
                } else {
                    loadingMoreRef.current = true;
                    setLoadingMore(true);
                }

                const params = {
                    start_date: startDate.format('YYYY-MM-DD'),
                    end_date: endDate.format('YYYY-MM-DD'),
                    transaction_type: selectedType || undefined,
                    q: searchTerm || undefined,
                    account_id: selectedAccount || undefined,
                    category_id: selectedCategory || undefined,
                    limit: PAGE_SIZE,
                    offset: offsetRef.current,
                };
                const response = await serverClient.get('/api/user/transaction', { params });

                // A newer request was started while this one was in flight — discard this response.
                if (requestId !== requestIdRef.current) return;

                const pageData = response?.data?.transactions || {};
                const pageCount = Object.values(pageData).reduce((sum, arr) => sum + arr.length, 0);

                setTransactionsData((prev) => {
                    if (reset) return pageData;
                    const merged = { ...prev };
                    Object.keys(pageData).forEach((date) => {
                        merged[date] = merged[date]
                            ? [...merged[date], ...pageData[date]]
                            : pageData[date];
                    });
                    return merged;
                });

                offsetRef.current += pageCount;
                const stillHasMore = pageCount === PAGE_SIZE;
                hasMoreRef.current = stillHasMore;
            } catch (e) {
                if (requestId !== requestIdRef.current) return;
                if (e.response?.status === 401) {
                    DeleteToken();
                    setError(t('userNotAuthorized'));
                    navigate('/login');
                } else {
                    setError(e.response?.data?.message || t('somethingWentWrong'));
                }
            } finally {
                if (reset) {
                    // Only the latest reset request may clear the busy flags,
                    // otherwise a stale request could hide the loader too early.
                    if (requestId === requestIdRef.current) {
                        resetInFlightRef.current = false;
                        setBusy(false);
                    }
                } else {
                    loadingMoreRef.current = false;
                    setLoadingMore(false);
                }
            }
        },
        [
            startDate,
            endDate,
            selectedType,
            searchTerm,
            selectedAccount,
            selectedCategory,
            navigate,
            t,
        ]
    );

    // Always call the latest fetch function from the scroll handler / debounce timer without
    // needing to re-bind them every time filters change.
    const fetchTransactionsPageRef = useRef(fetchTransactionsPage);
    useEffect(() => {
        fetchTransactionsPageRef.current = fetchTransactionsPage;
    }, [fetchTransactionsPage]);

    const handleTransactionAction = useCallback(() => {
        fetchTransactionsPageRef.current({ reset: true });
    }, []);

    const handleLoadMore = useCallback(() => {
        fetchTransactionsPageRef.current({ reset: false });
    }, []);

    // --- Effects ---
    // Initialize: Fetch accounts and categories only once on mount
    useEffect(() => {
        feather.replace();
        const initialize = async () => {
            try {
                const [accRes, catRes] = await Promise.all([
                    serverClient.get('/api/user/accounts'),
                    serverClient.get('/api/user/categories'),
                ]);
                setAccounts(accRes.data.accounts);
                setCategories(catRes.data.categories);
            } catch (e) {
                if (e.response?.status === 401) {
                    DeleteToken();
                    setError(t('userNotAuthorized'));
                    navigate('/login');
                } else {
                    setError(t('somethingWentWrong'));
                }
            }
        };
        initialize();
    }, [navigate, t]);

    // Fetch transaction history (from page 1) when filters change (excluding searchTerm)
    useEffect(() => {
        fetchTransactionsPageRef.current({ reset: true });
    }, [startDate, endDate, selectedType, selectedAccount, selectedCategory]);

    // Debounce search term changes ONLY
    useEffect(() => {
        if (isFirstSearch.current) {
            isFirstSearch.current = false;
            return;
        }
        const delayDebounce = setTimeout(() => {
            fetchTransactionsPageRef.current({ reset: true, useSearchLoading: true });
        }, 900);
        return () => clearTimeout(delayDebounce);
    }, [searchTerm]);

    // Refresh data when signal changes (explicit user action like add/update/delete)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        // PrivateOutlet initializes refreshSignal to 0, so it is never undefined.
        // Skip the first run — the mount effects already fetch everything —
        // otherwise every API call fires twice on page load.
        if (isFirstRefreshSignal.current) {
            isFirstRefreshSignal.current = false;
            return;
        }
        setError(false);
        const refreshData = async () => {
            try {
                const [accRes, catRes] = await Promise.all([
                    serverClient.get('/api/user/accounts'),
                    serverClient.get('/api/user/categories'),
                ]);
                setAccounts(accRes.data.accounts);
                setCategories(catRes.data.categories);
                await fetchTransactionsPageRef.current({ reset: true });
            } catch (e) {
                if (e.response?.status === 401) {
                    DeleteToken();
                    setError(t('userNotAuthorized'));
                    navigate('/login');
                } else {
                    setError(e.response?.data?.message || t('somethingWentWrong'));
                }
            }
        };
        refreshData();
    }, [refreshSignal, navigate]);

    // Infinite scroll: load the next page when the user nears the bottom of the page.
    // Works the same on mobile and desktop - the browser window is the scroll container.
    useEffect(() => {
        const handleScroll = () => {
            if (loadingMoreRef.current || !hasMoreRef.current) return;
            const scrolledToBottom =
                window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 300;
            if (scrolledToBottom) {
                handleLoadMore();
            }
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleLoadMore]);

    // --- Render ---
    const pageMeta = getPageMeta('transactionHistory', language);
    const loadedCount = Object.values(transactionsData).reduce((sum, arr) => sum + arr.length, 0);

    return (
        <>
            <Helmet>
                <title>{pageMeta.title}</title>
                <meta name="description" content={pageMeta.description} />
                <meta name="keywords" content={pageMeta.keywords} />
                <link rel="canonical" href={pageMeta.url} />
                {pageMeta.noindex && <meta name="robots" content="noindex, follow" />}

                {/* Open Graph Tags */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content={pageMeta.title} />
                <meta property="og:description" content={pageMeta.description} />
                <meta property="og:url" content={pageMeta.url} />
                <meta property="og:image" content={pageMeta.image} />

                {/* Twitter Card Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={pageMeta.title} />
                <meta name="twitter:description" content={pageMeta.description} />
                <meta name="twitter:image" content={pageMeta.image} />

                {/* Structured Data for Search Engines */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'CollectionPage',
                        name: 'Transaction History',
                        description: pageMeta.description,
                        url: pageMeta.url,
                        image: pageMeta.image,
                    })}
                </script>
            </Helmet>

            <Loader loading={loading || searchLoading} />
            <header className="section-t-space">
                <div className="custom-container">
                    <div className="header-panel">
                        <BackButton fallback="/" />
                        <h2>{t('transactionHistory')}</h2>
                        <div className="dropdown">
                            <Link
                                to="#"
                                className="back-btn"
                                role="button"
                                data-bs-toggle="dropdown"
                            >
                                <i className="icon" data-feather="settings"></i>
                            </Link>
                            <ul className="dropdown-menu">
                                <li>
                                    <Link
                                        className="dropdown-item"
                                        to="#"
                                        onClick={handleThisMonth}
                                    >
                                        {t('thisMonth')}
                                    </Link>
                                </li>
                                <li>
                                    <Link className="dropdown-item" onClick={handleThisYear} to="#">
                                        {t('thisYear')}
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        className="dropdown-item"
                                        onClick={handlePreviousSixMonth}
                                        to="#"
                                    >
                                        {t('lastSixMonth')}
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        className="dropdown-item"
                                        to="#period"
                                        data-bs-toggle="modal"
                                    >
                                        {t('custom')}
                                    </Link>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </header>
            <div className="transaction-history-page">
                <CustomDateModal
                    customStartDate={customStartDate}
                    customEndDate={customEndDate}
                    setCustomStartDate={setCustomStartDate}
                    setCustomEndDate={setCustomEndDate}
                    handleApplyCustomDate={handleApplyCustomDate}
                    t={t}
                />
                <div className="custom-container">
                    <div className="th-filter-card">
                        <SearchInput
                            searchTerm={searchTerm}
                            handleSearchChange={handleSearchChange}
                            t={t}
                        />
                        <Filters
                            accountOptions={accountOptions}
                            categoryOptions={categoryOptions}
                            selectedType={selectedType}
                            setSelectedType={setSelectedType}
                            selectedAccount={selectedAccount}
                            setSelectedAccount={setSelectedAccount}
                            selectedCategory={selectedCategory}
                            setSelectedCategory={setSelectedCategory}
                            resetFilters={resetFilters}
                            t={t}
                        />
                    </div>
                </div>

                <section>
                    <div className="custom-container">
                        <div className="th-period-strip">
                            <div className="th-period-text">
                                <div className="th-period-icon">
                                    <FiCalendar />
                                </div>
                                <h5>{periodLabel}</h5>
                            </div>
                            <span className="th-period-count">
                                {numberLanguageWise(loadedCount, language)} {t('transactions')}
                            </span>
                        </div>
                    </div>
                </section>

                <section className="section-b-space">
                    <div className="custom-container">
                        {error && <Alert alert={'danger'}>{error}</Alert>}
                        {Object.keys(transactionsData).length > 0 ? (
                            Object.keys(transactionsData).map((date) => (
                                <div key={date}>
                                    <div className="title margin-top-5">
                                        <h2>{dateDisplay(formatDate(date), language)}</h2>
                                    </div>
                                    <div className="row gy-3">
                                        {transactionsData[date].map((transaction) => {
                                            const localDateString =
                                                transaction.transaction_time.replace('Z', '');
                                            const bdDate = new Date(localDateString);
                                            const formattedDate = bdDate.toLocaleTimeString(
                                                'en-US',
                                                {
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                    timeZone: getUserTimezone(),
                                                }
                                            );
                                            return (
                                                <div className="col-12" key={transaction.id}>
                                                    <div
                                                        className="transaction-box"
                                                        onClick={() =>
                                                            handleShowBottomSheet(transaction)
                                                        }
                                                    >
                                                        <div className="d-flex gap-3">
                                                            <div className="transaction-image">
                                                                <img
                                                                    className="img-fluid transaction-icon"
                                                                    src={getTransactionImage(
                                                                        transaction.category_id
                                                                    )}
                                                                    alt={
                                                                        transaction.transaction_type
                                                                    }
                                                                />
                                                            </div>
                                                            <div className="transaction-details">
                                                                <div className="transaction-name">
                                                                    <h5>
                                                                        {transaction.category_name ||
                                                                            'Transfer/Withdraw'}
                                                                    </h5>
                                                                    <h3
                                                                        className={getTransactionClass(
                                                                            transaction.transaction_type
                                                                        )}
                                                                    >
                                                                        {amountDisplay(
                                                                            transaction.amount,
                                                                            language
                                                                        )}
                                                                    </h3>
                                                                </div>
                                                                <div className="d-flex justify-content-between">
                                                                    <h5 className="light-text">
                                                                        {transaction.category_name ===
                                                                        null
                                                                            ? `${transaction.account_id === 0 ? t('outOfWallet') : transaction.account_name} -> ${transaction.to_account_id === 0 ? t('outOfWallet') : transaction.to_account_name}`
                                                                            : transaction.account_name}
                                                                    </h5>
                                                                    <h5 className="light-text">
                                                                        {numberLanguageWise(
                                                                            formattedDate,
                                                                            language
                                                                        )}
                                                                    </h5>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))
                        ) : !loading ? (
                            <EmptyState t={t} />
                        ) : null}
                        {loadingMore && (
                            <div className="text-center py-3">
                                <div className="spinner-border spinner-border-sm" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        )}
                        {/*{!loading && !loadingMore && !hasMore && Object.keys(transactionsData).length > 0 && (*/}
                        {/*    <div className="text-center py-3 light-text">{t("noMoreTransactions")}</div>*/}
                        {/*)}*/}
                        <div className="fixed-month-selector">
                            <button className="nav-button" onClick={handlePrevPeriod}>
                                <i data-feather="chevron-left"></i>
                            </button>
                            <div className="month-wrapper">{periodLabel}</div>
                            <button className="nav-button" onClick={handleNextPeriod}>
                                <i data-feather="chevron-right"></i>
                            </button>
                        </div>
                    </div>
                </section>
            </div>
            {transaction && (
                <UpdateTransactionBottomSheet
                    show={showModal}
                    onClose={handleCloseBottomSheet}
                    onTransactionUpdate={handleTransactionAction}
                    setShowToast={setShowToast}
                    transaction={transaction}
                    setShowToastForDelete={setShowToastForDelete}
                />
            )}
            {showToast && (
                <SuccessToast
                    message={t('transactionUpdated')}
                    onClose={() => setShowToast(false)}
                />
            )}
            {showToastForDelete && (
                <WarningToast
                    icon={'https://cdn-icons-png.flaticon.com/512/6861/6861362.png'}
                    message={t('transactionRemoved')}
                    onClose={() => setShowToastForDelete(false)}
                />
            )}
            <BottomNavbar />
        </>
    );
};

export default TransactionHistory;
